import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router } from '@angular/router';
import { format, parseISO } from 'date-fns';
import { CountryVm } from 'src/app/api';
import { CombinedStore } from 'src/app/core/combined.store';
import { CANAL_VENTE_C } from 'src/app/shared/constants/context.constants';
import { ERROR_EMAIL_PATH } from 'src/app/shared/constants/route.constants';
import { PathType } from 'src/app/shared/enum/path-type.enum';
import { environment } from 'src/environments/environment';
import { HmBrand, isPartner } from '../utils/brand-utils';
import { ContextsService } from './../../api/api/contexts.service';
import {
  civiliteObject,
  Civility,
} from './../models/model/concretes/shared/civility.model';

@Injectable({
  providedIn: 'root',
})
export class ContextGuard implements CanActivate {
  private store = inject(CombinedStore);

  civilities: Civility[] = civiliteObject;
  constructor(
    private router: Router,
    private contextService: ContextsService,
  ) {}

  canActivate(route: ActivatedRouteSnapshot): boolean {
    const contextId = route.params['id'];
    if (!environment.production) {
      console.log('ContextResolver: resolve', contextId);
    }
    this.contextService
      .apiContextsIdGet(contextId)
      .pipe()
      .subscribe((ctx) => {
        if (ctx) {
          sessionStorage.clear();
          this.store.reset();
          this.store.updateContext(ctx);
          this.store.updateContext({
            token: contextId,
          });

          if (this.store.context().souscripteur?.email) {
            this.updateSubscriberWithContext();
          } else if (
            this.store.context().canalVente === CANAL_VENTE_C &&
            isPartner(environment.brand)
          ) {
            // In partner mode, if no email in context, redirect to error with specific message
            this.router.navigate(['/' + ERROR_EMAIL_PATH]);
            return;
          }
          // Update Path
          this.store.updatePathType(ctx.produit as PathType);
          // Redirection
          this.router.navigate(['/start', ctx.token]);
          return true;
        }
        // Empêche l'accès au composant cible après redirection
        return false;
      });
    return false;
  }

  private updateSubscriberWithContext() {
    const dateString = this.store.context().souscripteur?.dateNaissance ?? '';
    const date = dateString != '' ? parseISO(dateString) : '';
    const formattedDate = dateString != '' ? format(date, 'dd/MM/yyyy') : '';
    let phone = '';

    if (
      this.store.context().souscripteur?.telephone &&
      this.store.context().codeVendeur !== HmBrand
    ) {
      phone = this.store.context().souscripteur?.telephone ?? '';
    }
    this.store.updateSubscriber({
      email: this.store.context().souscripteur?.email ?? '',
      civility:
        this.civilities.find(
          (m) => m.id == this.store.context().souscripteur?.civilite,
        ) ?? null,
      lastname: this.store.context().souscripteur?.nom ?? '',
      firstname: this.store.context().souscripteur?.prenom ?? '',
      birth_date: formattedDate,
      birth_postal_code: null,
      birth_city: this.store.context().souscripteur?.villeNaissance ?? '',
      birth_country:
        this.mapToCountryVm(
          this.store.context().souscripteur?.paysNaissance ?? '',
        ) ?? null,
      nationality: null,
      phone_prefix: {
        indicator: null,
        phone: phone,
        phone_consent: false,
      },
      phone_number: phone,
      consent: {
        marketing_phone_consent: false,
        marketing_email_consent: false,
      },
      is_client: false,
      from_context: true,
    });

    if (this.store.context().souscripteur?.adresse) {
      const adresse = this.store.context().souscripteur?.adresse ?? '';
      const codePostal = this.store.context().souscripteur?.codePostal ?? '';
      const ville = this.store.context().souscripteur?.ville ?? '';
      this.store.updateAddress({
        addressAutocomplete: {
          place: `${adresse}, ${codePostal} ${ville}`,
          streetNumber: adresse.split(' ').slice(0, 1).join(' '),
          street: adresse.split(' ').slice(1).join(' '),
          zipCode: codePostal,
          city: ville,
        },
        address: this.store.context().souscripteur?.adresse ?? '',
        city: this.store.context().souscripteur?.ville ?? '',
        country: this.mapToCountryVm(
          this.store.context().souscripteur?.pays ?? '',
        ),
        zipCode: this.store.context().souscripteur?.codePostal ?? '',
      });
    }
  }

  private mapToCountryVm(countryName: string | undefined): CountryVm | null {
    if (!countryName) {
      return null;
    }

    return {
      countryCode: countryName == 'France' ? 'FR' : null,
      countryNameFr: countryName,
    };
  }
}
